import React, {
    createContext,
    Dispatch,
    ReactNode,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useId,
    useMemo,
    useState,
} from 'react';
import { z } from 'zod';
import {
    GetOrderResponseSchema,
    GetProductGroupListResponseSchema,
    PRODUCT_CATEGORY,
    SUPPORTED_UNITS,
    FulfillmentProductConfiguration,
    LineItemListingSchema,
    ORDER_SOURCE_SCHEMA,
    GetFulfillmentProductGroupsResponseSchema,
    FREIGHT_TYPE,
    DISPOSAL_PURPOSE,
    ORDER_STATUS_SCHEMA,
    PostQuoteFulfillmentRequest,
    PatchQuoteFulfillmentRequestSchema,
    PostPositionFulfillmentRequestSchema,
    GetPositionResponse,
    CUSTOM_REQUEST_PRODUCT_TYPES,
    CUSTOM_REQUEST_PRODUCT_TYPES_SCHEMA,
} from '@schuettflix/interfaces';
import { CurrencyCode } from '@schuettflix/utils';
import { useOrder } from '@/clients/order/useOrder';
import { useOrderProductGroup } from '@/clients/orderProducts/useOrderProductGroup.ts';
import { Price } from '@schuettflix/conversion';
import { useNavigate } from 'react-router';
import { useGetQuotesByOrder } from '@/clients/quotes/useQuotes.ts';
import { useGetFulfillmentProductGroupByOrderId } from '@/clients/fulfillment/useFulfillmentProductGroup.ts';
import { mapToFulfillmentQuote, PayloadQuote } from '@/modules/fulfillment/utils/mapToFulfillmentQuote.ts';
import { FulfillmentDiffData } from '../hooks/useFulfillmentDiffCheck';
import { useGetPositionQueryOptions, usePosition } from '@/clients/position/usePosition';
import { usePositionProductGroups } from '@/clients/position/usePositionProductGroup';
import { usePositionQuotes } from '@/CustomRequestProductChannel/hooks/usePositionQuote';
import { useDeepCompareMemo } from 'use-deep-compare';
import { Resolve } from '@/shared/types/Resolve';
import { v4 as uuidv4 } from 'uuid';
import { captureException } from '@sentry/react';
import { DeepOmit } from '@/shared/types/DeepOmit';
import { UploadFileSource } from '@/modules/fulfillment/context/FulfillmentUploadContext.tsx';
import { useSuspenseQueries } from '@tanstack/react-query';

export type LineItemListing = z.infer<typeof LineItemListingSchema>;

export const PRODUCT_SECTION_PREFIX = `product-section-item`;

export function getProductSectionName(productId: string) {
    return `${PRODUCT_SECTION_PREFIX}-${productId}`;
}

export type OrderType = DeepOmit<z.infer<typeof GetOrderResponseSchema>, 'customerInfo.orderId'>;

export type FulfillmentQuote = {
    id: string;
    name: string;
    orderProductId: string;
    amount: number;
    unit: SUPPORTED_UNITS;
    currencyCode: CurrencyCode;
    partnerOrganizationId: number;
    orderingOrganizationId: number;
    platformOrganizationId: number;
    purchasePrice: Price;
    purchaseTaxClassId: string;
    salesPrice: Price;
    salesTaxClassId: string;
    serviceDate: string;
    freightType?: FREIGHT_TYPE;
    disposalPurpose?: DISPOSAL_PURPOSE;
};

type ReducedFulfillmentProductConfiguration = Omit<FulfillmentProductConfiguration, 'fulfillmentProductId'>; // the fulfillmentProductId is not used, just creates ambiguity in position context

export type FulfillmentProduct = {
    id: string;
    index: number;
    category: PRODUCT_CATEGORY;
    type: CUSTOM_REQUEST_PRODUCT_TYPES;
    templateId?: string;
    inOriginalOrder: boolean;
    positionProductId: string | null;
    quote?: FulfillmentQuote;
    fulfillmentProductConfiguration: ReducedFulfillmentProductConfiguration;
};

export type OrderFulfillmentProduct = Omit<FulfillmentProduct, 'quote'> & { quote: FulfillmentQuote };

export type UpdateOrderFulfillmentProduct = Partial<Omit<OrderFulfillmentProduct, 'id'>> & { id: string };

export type FormError = {
    field: string;
    message: string;
    scrollToElement: () => void;

    /** An optional arbitrary id that can help identifying
     * if the error belongs to the current section / component instance
     */
    sectionId?: string;
};

type CustomerInfoOverride = Partial<{ manualCostCenter: string | null; userId: number }>;

type FulfillmentContextBase = {
    contextSource: 'order' | 'position';
    order: OrderType;
    position?: GetPositionResponse;
    overrideCustomerInfo: (customerInfoOverride: CustomerInfoOverride) => void;
    products: FulfillmentProduct[];
    orderFulfillmentProducts: OrderFulfillmentProduct[];
    fulfillmentProducts: FulfillmentProduct[];
    documents: DocumentType[];
    setDocuments: (values: DocumentType[]) => void;
    errors: FormError[];
    setErrors: Dispatch<SetStateAction<FormError[]>>;
    updateOrderFulfillmentProduct: (product: UpdateOrderFulfillmentProduct) => void;
    /** Whether the form submit button was clicked */
    isSubmitted: boolean;
    setSubmitted: (value: boolean) => void;
    invalidSections: string[];
    setInvalidSections: Dispatch<SetStateAction<string[]>>;
    isPageValid: boolean;
    canSubmit: boolean;
    setCanSubmitStack: Dispatch<SetStateAction<Record<string, boolean>>>;
    quotesByOrder: FulfillmentQuote[];
    /**
     * Will be used when "individual line item listing" mode is enabled.
     * If the mode is not enabled, this value will be null.
     */
    lineItemListing: LineItemListing[] | null;
    /**
     * Will be used when "individual line item listing" mode is enabled.
     * If the mode is not enabled, the line items will be auto-inferred from the products.
     */
    getLineItemListingWithProductFallback: () => LineItemListing[];
    setLineItemListing: Dispatch<SetStateAction<LineItemListing[] | null>>;
    fulfillmentDiffDataStack: Record<string, FulfillmentDiffData>;
    setFulfillmentDiffDataStack: Dispatch<SetStateAction<Record<string, FulfillmentDiffData>>>;
};

type ReducedProductGroup = Resolve<
    Pick<z.input<typeof GetProductGroupListResponseSchema>, 'id' | 'type' | 'templateId' | 'orderId' | 'name'>
>;

type PatchQuoteFulfillmentRequest = z.input<typeof PatchQuoteFulfillmentRequestSchema>;
type PostPositionFulfillmentQuotePayload = z.input<
    typeof PostPositionFulfillmentRequestSchema
>['products'][number]['quotePayload'];

type DirectFulfillmentQuote = Omit<PostQuoteFulfillmentRequest, 'orderId' | 'productType'> & {
    id: string;
    name: string;
    productType: CUSTOM_REQUEST_PRODUCT_TYPES;
    freightType: PayloadQuote['freightType'];
    disposalPurpose: PayloadQuote['disposalPurpose'];
};

type FulfillmentContextPosition = FulfillmentContextBase & {
    contextSource: 'position';
    productGroup: Omit<ReducedProductGroup, 'orderId'>;
    addFulfillmentProduct: (product: Omit<ReducedOriginalProduct, 'id'>) => void;
    removeFulfillmentProduct: (productId: string) => void;
    updateFulfillmentProduct: (productId: string, updatedProduct: Partial<Omit<ReducedOriginalProduct, 'id'>>) => void;
    addFulfillmentQuote: (quote: Omit<DirectFulfillmentQuote, 'id'>) => void;
    removeFulfillmentQuote: (id: string) => void;
    updateFulfillmentQuoteByOrderProductId: (
        orderProductId: string,
        updatedQuote: PatchQuoteFulfillmentRequest
    ) => void;
    getFulfillmentQuoteByOrderProductId: (productId: string) => PostPositionFulfillmentQuotePayload;
    order: {
        source: typeof ORDER_SOURCE_SCHEMA.Enum.DIRECT_POSITION_FULFILLMENT;
        status: typeof ORDER_STATUS_SCHEMA.enum.SUBMITTED;
    };
};

type FulfillmentContextOrder = FulfillmentContextBase & {
    contextSource: 'order';
    productGroup: ReducedProductGroup;
    addFulfillmentProduct?: undefined;
    removeFulfillmentProduct?: undefined;
    updateFulfillmentProduct?: undefined;
    addFulfillmentQuote?: undefined;
    removeFulfillmentQuote?: undefined;
    updateFulfillmentQuoteByOrderProductId?: undefined;
    getFulfillmentQuoteByOrderProductId?: undefined;
};

type FulfillmentContextType = FulfillmentContextPosition | FulfillmentContextOrder;

export type DocumentType = {
    url: string;
    fileName: string;
    source: UploadFileSource;
};

const FulfillmentContext = createContext<FulfillmentContextType | undefined>(undefined);

type ReducedOriginalProduct = Resolve<
    Pick<
        z.infer<typeof GetFulfillmentProductGroupsResponseSchema>[number]['products'][number],
        'id' | 'index' | 'productCategory' | 'productType'
    > & { fulfillmentProductConfiguration: ReducedFulfillmentProductConfiguration }
>;

const useSharedFulfillmentContextProviderLogic = <TOrder extends OrderType>({
    originalOrderFulfillmentProducts,
    originalFulfillmentProducts,
    quotes,
    order,
}: {
    originalOrderFulfillmentProducts: ReducedOriginalProduct[];
    originalFulfillmentProducts: ReducedOriginalProduct[];
    quotes: PayloadQuote[] | null;
    order: TOrder;
}) => {
    // products
    const [orderFulfillmentProductsOverride, setOrderFulfillmentProductsOverride] = useState<
        Record<string, UpdateOrderFulfillmentProduct | undefined>
    >({});
    const orderFulfillmentProducts = useMemo<OrderFulfillmentProduct[]>(() => {
        if (!quotes || !originalOrderFulfillmentProducts) {
            return [];
        }
        return originalOrderFulfillmentProducts.map(product => {
            const quote = quotes?.find(quote => quote.orderProductId === product.id);

            if (!quote) {
                throw new Error(`quote not found for original order product with Id: ${product.id}`);
            }

            const override = orderFulfillmentProductsOverride[product.id];

            return {
                id: product.id,
                index: product.index,
                category: product.productCategory,
                type: CUSTOM_REQUEST_PRODUCT_TYPES_SCHEMA.parse(product.productType),
                inOriginalOrder: true,
                positionProductId: null,
                fulfillmentProductConfiguration: product.fulfillmentProductConfiguration,
                quote: mapToFulfillmentQuote(quote),
                ...override,
            };
        });
    }, [orderFulfillmentProductsOverride, originalOrderFulfillmentProducts, quotes]);
    const updateOrderFulfillmentProduct = (product: UpdateOrderFulfillmentProduct) => {
        setOrderFulfillmentProductsOverride(previousProductsOverride => {
            return {
                ...previousProductsOverride,
                [product.id]: { ...previousProductsOverride[product.id], ...product },
            };
        });
    };
    const fulfillmentProducts = useMemo<FulfillmentProduct[]>(
        () =>
            originalFulfillmentProducts.map(product => {
                const quote = quotes?.find(quote => quote.orderProductId === product.id);

                return {
                    id: product.id,
                    index: product.index,
                    category: product.productCategory,
                    type: CUSTOM_REQUEST_PRODUCT_TYPES_SCHEMA.parse(product.productType),
                    inOriginalOrder: false,
                    positionProductId: null,
                    fulfillmentProductConfiguration: product.fulfillmentProductConfiguration,
                    quote: quote ? mapToFulfillmentQuote(quote) : undefined,
                };
            }),
        [originalFulfillmentProducts, quotes]
    );
    const products = useMemo(() => {
        return [...orderFulfillmentProducts, ...fulfillmentProducts];
    }, [orderFulfillmentProducts, fulfillmentProducts]);
    const quotesByOrder = quotes?.map(mapToFulfillmentQuote) ?? [];

    // order
    const [customerInfoOverride, setCustomerInfoOverride] = useState<CustomerInfoOverride>({});
    const orderWithCustomerInfoOverride = useMemo(
        () => ({
            ...order,
            ...(order?.customerInfo && {
                customerInfo: {
                    ...order.customerInfo,
                    ...customerInfoOverride,
                },
            }),
        }),
        [order, customerInfoOverride]
    );

    // shared state
    const [documents, setDocuments] = useState<DocumentType[]>([]);
    const [errors, setErrors] = useState<FormError[]>([]);
    const [isSubmitted, setSubmitted] = useState<boolean>(false);
    const [fulfillmentDiffDataStack, setFulfillmentDiffDataStack] = useState<Record<string, FulfillmentDiffData>>({});

    // validity state
    const [invalidSections, setInvalidSections] = useState<string[]>([]);
    const [isPageValid, setIsPageValid] = useState<boolean>(true);
    useEffect(() => {
        setIsPageValid(invalidSections.length === 0);
    }, [invalidSections]);

    // canSubmit
    const [canSubmitStack, setCanSubmitStack] = useState<Record<string, boolean>>({});
    const canSubmit = Object.values(canSubmitStack).every(v => !!v);

    // lineItemListing
    const [lineItemListing, setLineItemListing] = useState<LineItemListing[] | null>(null);
    const getLineItemListingWithProductFallback = useCallback(() => {
        return (
            lineItemListing ??
            products.map(product =>
                LineItemListingSchema.parse({
                    ...product?.quote,
                    productId: product.id,
                    sortIndex: product.index,
                })
            )
        );
    }, [lineItemListing, products]);

    return {
        products,
        orderFulfillmentProducts,
        fulfillmentProducts,
        updateOrderFulfillmentProduct,
        quotesByOrder,

        order: orderWithCustomerInfoOverride,
        overrideCustomerInfo: setCustomerInfoOverride,
        documents,
        setDocuments,
        errors,
        setErrors,
        isSubmitted,
        setSubmitted,
        fulfillmentDiffDataStack,
        setFulfillmentDiffDataStack,
        invalidSections,
        setInvalidSections,
        isPageValid,
        canSubmit,
        setCanSubmitStack,
        lineItemListing,
        setLineItemListing,
        getLineItemListingWithProductFallback,
    };
};

export const FulfillmentContextProviderFromPosition: React.FC<{ positionId: string; children: ReactNode }> = ({
    positionId,
    children,
}) => {
    const { data: positionData } = usePosition(positionId, { fetchOnce: true });
    const { data: positionProductGroups } = usePositionProductGroups(positionId, { fetchOnce: true });

    const productGroup = (positionProductGroups ?? [])[0];

    const products = useMemo(() => {
        return productGroup?.products || [];
    }, [productGroup]);

    const originalOrderFulfillmentProducts = useMemo(
        () =>
            products.map(
                product =>
                    ({
                        ...product,
                        fulfillmentProductConfiguration: product.positionProductConfiguration,
                        productCategory: product.category,
                        productType: product.type,
                    }) satisfies ReducedOriginalProduct
            ),
        [products]
    );

    const [fulfillmentProductRecord, setFulfillmentProductRecord] = useState<Record<string, ReducedOriginalProduct>>(
        {}
    );
    const addFulfillmentProduct = useCallback((product: Omit<ReducedOriginalProduct, 'id'>) => {
        const id = uuidv4();
        setFulfillmentProductRecord(prev => ({ ...prev, [id]: { ...product, id } }));
    }, []);
    const removeFulfillmentProduct = useCallback((productId: string) => {
        setFulfillmentProductRecord(({ [productId]: _, ...prev }) => prev);
    }, []);
    const updateFulfillmentProduct = useCallback(
        (productId: string, updatedProduct: Partial<Omit<ReducedOriginalProduct, 'id'>>) => {
            setFulfillmentProductRecord(prev => ({
                ...prev,
                [productId]: { ...prev[productId], ...updatedProduct, id: productId },
            }));
        },
        []
    );
    const originalFulfillmentProducts = useMemo(() => {
        return Object.values(fulfillmentProductRecord);
    }, [fulfillmentProductRecord]);

    const positionQuotes = usePositionQuotes(
        products.map(({ id }) => id),
        { fetchOnce: true }
    ).map(({ data }) => data);

    const [fulfillmentQuoteRecord, setFulfillmentQuoteRecord] = useState<Record<string, DirectFulfillmentQuote>>({});
    const addFulfillmentQuote = useCallback<FulfillmentContextPosition['addFulfillmentQuote']>(quote => {
        const id = uuidv4();
        setFulfillmentQuoteRecord(prev => ({
            ...prev,
            [id]: { ...quote, id },
        }));
    }, []);
    const removeFulfillmentQuote = useCallback<FulfillmentContextPosition['removeFulfillmentQuote']>(id => {
        setFulfillmentQuoteRecord(prev => {
            const { [id]: _, ...rest } = prev;
            return rest;
        });
    }, []);
    const updateFulfillmentQuoteByOrderProductId = useCallback<
        FulfillmentContextPosition['updateFulfillmentQuoteByOrderProductId']
    >((orderProductId, quote) => {
        setFulfillmentQuoteRecord(prev => {
            const id = Object.values(prev).find(quote => quote.orderProductId === orderProductId)?.id;
            if (!id) {
                captureException(new Error(`Quote with orderProductId ${orderProductId} not found`));
                return prev;
            }
            return {
                ...prev,
                [id]: {
                    ...prev[id],
                    ...quote,
                    id,
                    ...(quote.payload !== undefined && quote.payload),
                    payload: {
                        ...prev[id].payload,
                        ...quote.payload,
                    },
                },
            };
        });
    }, []);

    const quotes = useDeepCompareMemo(() => {
        const quotesFromPosition = positionQuotes.flatMap(quote =>
            quote && quote.partnerOrganizationId
                ? ({
                      ...quote,
                      partnerOrganizationId: quote.partnerOrganizationId,
                      amount: 0,
                      serviceDate: new Date(),
                      // TODO[MAR-3388]: this is rather a hack. Do we need this id anywhere?
                      id: quote.positionProductId,
                      // TODO[MAR-3388]: this is rather a hack. Do we need this id anywhere?
                      orderProductId: quote.positionProductId,
                      name: quote.payload.name,
                      orderingOrganizationId: positionData.customerInfo.organizationId,
                      freightType: 'freightType' in quote.payload ? quote.payload.freightType : undefined,
                      disposalPurpose: 'disposalPurpose' in quote.payload ? quote.payload.disposalPurpose : undefined,
                  } satisfies PayloadQuote)
                : []
        );

        const quotesFromFulfillment = Object.values(fulfillmentQuoteRecord);

        return [...quotesFromPosition, ...quotesFromFulfillment];
    }, [positionData.customerInfo.organizationId, positionQuotes, fulfillmentQuoteRecord]);

    const order = useMemo(
        () => ({
            ...positionData,
            source: ORDER_SOURCE_SCHEMA.Enum.DIRECT_POSITION_FULFILLMENT,
            positionId,
            humanReadableId: '',
            status: 'SUBMITTED' as const,
            customerInfo: {
                ...positionData.customerInfo,
                manualCostCenter: null,
                projectCode: positionData.projectNumber,
            },
        }),
        [positionData, positionId]
    );

    const sharedContext = useSharedFulfillmentContextProviderLogic({
        originalOrderFulfillmentProducts,
        originalFulfillmentProducts,
        quotes,
        order,
    });

    const getFulfillmentQuoteByOrderProductId = useCallback<
        FulfillmentContextPosition['getFulfillmentQuoteByOrderProductId']
    >(
        orderProductId => {
            const id = Object.values(fulfillmentQuoteRecord).find(quote => quote.orderProductId === orderProductId)?.id;
            if (id) return fulfillmentQuoteRecord[id];

            const orderFulfillmentProduct = sharedContext.orderFulfillmentProducts.find(
                product => product.id === orderProductId
            );
            if (orderFulfillmentProduct) {
                const { quote, ...product } = orderFulfillmentProduct;
                return {
                    ...quote,
                    productType: product.type,
                    payload: {
                        ...quote,
                    },
                };
            }

            throw new Error(`Quote with orderProductId ${orderProductId} not found`);
        },
        [fulfillmentQuoteRecord, sharedContext.orderFulfillmentProducts]
    );

    const value: FulfillmentContextType = {
        contextSource: 'position',
        ...sharedContext,
        position: positionData,
        productGroup,
        addFulfillmentProduct,
        removeFulfillmentProduct,
        updateFulfillmentProduct,
        addFulfillmentQuote,
        removeFulfillmentQuote,
        updateFulfillmentQuoteByOrderProductId,
        getFulfillmentQuoteByOrderProductId,
    };

    return <FulfillmentContext.Provider value={value}>{children}</FulfillmentContext.Provider>;
};

export const FulfillmentContextProviderFromOrder: React.FC<{ orderId: string; children: ReactNode }> = ({
    orderId,
    children,
}) => {
    const navigate = useNavigate();

    const { data: orderData } = useOrder(orderId, { fetchOnce: true });
    const { data: productGroupsData } = useOrderProductGroup(orderId, true);
    const { data: fulfillmentProductGroup } = useGetFulfillmentProductGroupByOrderId(orderId);
    const { data: quotes } = useGetQuotesByOrder(orderId);

    // conditionally get position data based on order positionId
    // using the dynamic nature of `useSuspenseQueries`
    const getPositionQueryOptions = useGetPositionQueryOptions();
    const [{ data: positionData } = { data: undefined }] = useSuspenseQueries({
        queries: [orderData.positionId].flatMap(positionId => (positionId ? getPositionQueryOptions(positionId) : [])),
    });

    const originalFulfillmentProducts = useMemo(() => {
        return fulfillmentProductGroup?.products?.filter(p => p.source === 'FULFILLMENT') ?? [];
    }, [fulfillmentProductGroup?.products]);

    const originalOrderFulfillmentProducts = useMemo(
        () => fulfillmentProductGroup?.products?.filter(p => p.source === 'ORDER'),
        [fulfillmentProductGroup?.products]
    );

    const sharedContext = useSharedFulfillmentContextProviderLogic({
        originalOrderFulfillmentProducts,
        originalFulfillmentProducts,
        quotes,
        order: orderData,
    });

    useEffect(() => {
        if (orderData?.status === 'DRAFT') {
            void navigate(`/order/${orderId}`);
        }
    }, [orderData, orderId, navigate]);

    return (
        <FulfillmentContext.Provider
            value={{
                ...sharedContext,
                contextSource: 'order',
                position: positionData,
                productGroup: productGroupsData,
            }}
        >
            {children}
        </FulfillmentContext.Provider>
    );
};

/**
 * Hook to access the FulfillmentContext.
 * Throws an error if used outside of a FulfillmentContextProvider.
 *
 * @returns The context value.
 * @throws {Error} If used outside of a FulfillmentContextProvider.
 */
export const useFulfillmentContext = () => {
    const context = useMaybeFulfillmentContext();
    if (!context) {
        throw new Error('useFulfillmentContext must be used within a FulfillmentContext');
    }
    return context;
};

/**
 * Hook to access the FulfillmentContext.
 * Returns undefined if used outside of a FulfillmentContextProvider.
 *
 * @returns The context value or undefined.
 */
export const useMaybeFulfillmentContext = () => {
    const context = useContext(FulfillmentContext);
    const id = useId();

    const setCanSubmit = useCallback(
        (value: boolean) => {
            if (!context) {
                return;
            }
            context.setCanSubmitStack(previousStack => {
                return { ...previousStack, [id]: value };
            });
        },
        [context, id]
    );

    // remove the entry from the canSubmitStack on unmount
    useEffect(() => {
        if (!context) {
            return;
        }
        return () => {
            context.setCanSubmitStack(previousStack => {
                const { [id]: _, ...rest } = previousStack;
                return rest;
            });
        };
        // we only want to run this effect on mount / unmount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!context) return undefined;
    return { ...context, setCanSubmit };
};
